import React, { useEffect, useRef, useState } from "react";
import Carousel from "../carousel-component";
import "./solution-section.scss";

type DeviceFrame = "phone" | "notebook";

interface SolutionSectionProps {
  id?: string;
  category?: string;
  title: string;
  texts: string[];
  imageUrl?: string;
  carouselImages?: any[];
  carouselMaxWidth?: number;
  deviceFrame?: DeviceFrame;
  imageAlt?: string;
  showAccent?: boolean;
  mediaFirst?: boolean;
}

export function SolutionSection({
  id,
  category,
  title,
  texts,
  imageUrl,
  carouselImages,
  carouselMaxWidth,
  deviceFrame,
  imageAlt = "",
  showAccent = false,
  mediaFirst = false,
}: SolutionSectionProps) {
  const hasMedia = Boolean(imageUrl || carouselImages?.length);
  const hasCategory = Boolean(category);
  const hasAccent = hasCategory || showAccent;
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const sectionElement = sectionRef.current;

    if (!sectionElement) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.25 }
    );

    observer.observe(sectionElement);

    return () => observer.disconnect();
  }, []);

  // Envolve o conteudo (carrossel ou imagem) na moldura do dispositivo
  const withDeviceFrame = (content: React.ReactNode) => {
    if (deviceFrame === "phone") {
      return (
        <div className="device-frame device-frame-phone">
          <div className="device-frame-screen">
            {content}
            <span className="device-frame-island" aria-hidden="true" />
          </div>
        </div>
      );
    }

    if (deviceFrame === "notebook") {
      return (
        <div className="device-frame device-frame-notebook">
          <div className="device-frame-notebook-lid">
            <div className="device-frame-notebook-bezel" aria-hidden="true">
              <span className="device-frame-notebook-camera" />
            </div>
            <div className="device-frame-screen">{content}</div>
          </div>
          <div className="device-frame-notebook-base" aria-hidden="true" />
        </div>
      );
    }

    return <>{content}</>;
  };

  const media = (
    <div
      className={`solution-section-image-container ${
        carouselImages?.length ? "solution-section-carousel-container" : ""
      }`}
    >
      {carouselImages?.length ? (
        <div
          className="solution-section-carousel-frame"
          style={carouselMaxWidth ? { maxWidth: carouselMaxWidth } : undefined}
        >
          {withDeviceFrame(
            <Carousel images={carouselImages} width={100} minWidth={0} />
          )}
        </div>
      ) : (
        withDeviceFrame(
          <img
            src={imageUrl}
            alt={imageAlt}
            className="solution-section-image"
          />
        )
      )}
    </div>
  );

  return (
    <div
      ref={sectionRef}
      id={id || category}
      className={`solution-section ${
        !hasMedia ? "solution-section-full-text" : ""
      } ${!hasAccent ? "solution-section-without-accent" : ""} ${
        !hasCategory ? "solution-section-without-category" : ""
      } ${mediaFirst ? "solution-section-media-first" : ""} ${
        isVisible ? "solution-section-visible" : ""
      }`}
    >
      {hasCategory && (
        <span className="solution-section-label">{category}</span>
      )}

      <div className="solution-section-copy">
        <h2>{title}</h2>

        {hasMedia && mediaFirst && media}

        {texts.map((text, index) => (
          <p
            key={index}
            className="solution-section-text"
            style={{ animationDelay: `${180 + index * 180}ms` }}
          >
            {text}
          </p>
        ))}
      </div>

      {hasMedia && !mediaFirst && media}
    </div>
  );
}
